/**
 * Testimonial submission modal — used on the Results page, triggered 6s after
 * results load AND once again after PDF download.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PURPLE_CYAN = 'linear-gradient(135deg, #7A00EB 0%, #00c3ef 100%)';

const STORAGE_KEY_SUBMITTED = 'testimonial_submitted';

export function shouldShowTestimonial() {
  try { return !window.localStorage.getItem(STORAGE_KEY_SUBMITTED); } catch (err) { console.warn('[TestimonialModal] localStorage unavailable:', err); return true; }
}

export function TestimonialModal({ open, onOpenChange, source = 'results_page' }) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [rating, setRating] = useState(5);
  const [hovered, setHovered] = useState(0);
  const [text, setText] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 8) {
      toast.error('Please write at least a sentence.');
      return;
    }
    if (!name.trim()) {
      toast.error('Please add your name.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/testimonials`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, company, rating, text, consent_to_publish: consent, source }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to send');
      try { window.localStorage.setItem(STORAGE_KEY_SUBMITTED, '1'); } catch (err) { console.warn('[TestimonialModal] Could not persist submitted flag:', err); }
      toast.success('Thanks! Your feedback helps other founders find their archetype.');
      onOpenChange(false);
    } catch (e) {
      toast.error('Could not send. Try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800" data-testid="testimonial-modal">
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-900" style={{ background: PURPLE_CYAN }}>
          <DialogTitle className="text-white text-lg font-bold leading-tight">Loved your archetype?</DialogTitle>
          <DialogDescription className="text-white/85 text-sm mt-1">
            One line about your experience — help other founders find theirs.
          </DialogDescription>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(n => (
              <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHovered(n)} onMouseLeave={() => setHovered(0)}
                      data-testid={`rating-star-${n}`} className="p-1">
                <Star className={`w-7 h-7 ${(hovered || rating) >= n ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-700'}`} />
              </button>
            ))}
          </div>

          <div>
            <Label htmlFor="t-name" className="text-xs text-gray-600 dark:text-gray-300 font-medium">Your name *</Label>
            <Input id="t-name" data-testid="testimonial-name" value={name} onChange={(e)=>setName(e.target.value)} placeholder="Alex Founder" maxLength={80} className="mt-1" />
          </div>

          <div>
            <Label htmlFor="t-company" className="text-xs text-gray-600 dark:text-gray-300 font-medium">Company (optional)</Label>
            <Input id="t-company" data-testid="testimonial-company" value={company} onChange={(e)=>setCompany(e.target.value)} placeholder="Acme Co." maxLength={80} className="mt-1" />
          </div>

          <div>
            <Label htmlFor="t-text" className="text-xs text-gray-600 dark:text-gray-300 font-medium">Your one-liner *</Label>
            <Textarea id="t-text" data-testid="testimonial-text" value={text} onChange={(e)=>setText(e.target.value)} rows={3} maxLength={280}
                      placeholder="Felt like a brand strategist sat with me for an hour…" className="mt-1 resize-none" />
            <p className="text-[10px] text-gray-400 mt-1 text-right">{text.length}/280</p>
          </div>

          <label className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300 leading-snug cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e)=>setConsent(e.target.checked)} data-testid="testimonial-consent" className="mt-0.5 accent-purple-600" />
            <span>I'm OK with you showing this (and my first name) on the website.</span>
          </label>

          <div className="flex gap-2 pt-1">
            <Button type="button" variant="ghost" className="flex-1" onClick={()=>onOpenChange(false)} data-testid="testimonial-skip">Maybe later</Button>
            <Button type="submit" disabled={submitting} className="flex-1 text-white" data-testid="testimonial-submit" style={{ background: PURPLE_CYAN }}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
